// CB:BEGIN impersonation-banner
/**
 * ImpersonationBanner
 *
 * Fixed top banner shown when an admin is in an impersonation session.
 * - Shows who is being impersonated and their role
 * - Shows countdown of time remaining in the session
 * - Provides a clear "Exit Session" button
 * - Pushes page content down by its own height via a body margin shim
 */
import React, { useEffect, useRef, useState } from 'react';
import { useImpersonation } from '../context/ImpersonationContext';

const BANNER_HEIGHT = 44; // px — keep in sync with actual rendered height
const SESSION_KEY = 'cb_impersonation';
const TTL_MS = 30 * 60 * 1000;

const ROLE_LABELS: Record<string, string> = {
  HOMEOWNER: 'Homeowner',
  CONTRACTOR: 'Contractor',
};

function useSessionTimeLeft(): number {
  const [msLeft, setMsLeft] = useState(0);

  useEffect(() => {
    const calc = () => {
      try {
        const raw = sessionStorage.getItem(SESSION_KEY);
        if (!raw) return 0;
        const { startedAt } = JSON.parse(raw);
        const elapsed = Date.now() - (startedAt ?? 0);
        return Math.max(0, TTL_MS - elapsed);
      } catch {
        return 0;
      }
    };

    setMsLeft(calc());
    const id = setInterval(() => setMsLeft(calc()), 10_000); // update every 10s
    return () => clearInterval(id);
  }, []);

  return msLeft;
}

function formatTimeLeft(ms: number): string {
  if (ms <= 0) return 'expired';
  const mins = Math.ceil(ms / 60_000);
  if (mins >= 60) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${mins}m left`;
}

const ImpersonationBanner: React.FC = () => {
  const { isImpersonating, impersonatedUser, exitSession } = useImpersonation();
  const [exiting, setExiting] = useState(false);
  const msLeft = useSessionTimeLeft();
  const isExpiringSoon = msLeft > 0 && msLeft < 5 * 60_000; // < 5 min

  // Push page body content down by banner height when banner is active
  useEffect(() => {
    if (!isImpersonating) return;
    const prev = document.body.style.marginTop;
    document.body.style.marginTop = `${BANNER_HEIGHT}px`;
    return () => {
      document.body.style.marginTop = prev;
    };
  }, [isImpersonating]);

  if (!isImpersonating || !impersonatedUser) return null;

  const handleExit = async () => {
    setExiting(true);
    try {
      await exitSession();
    } finally {
      setExiting(false);
      window.location.reload();
    }
  };

  const roleLabel = ROLE_LABELS[impersonatedUser.role] ?? impersonatedUser.role;
  const timeLabel = formatTimeLeft(msLeft);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="impersonation-banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        height: BANNER_HEIGHT,
        background: isExpiringSoon ? '#7c2d12' : '#92400e',
        color: '#ffffff',
        padding: '0 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 13,
        fontWeight: 500,
        boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
        gap: 12,
      }}
    >
      {/* Left: session info */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden' }}>
        <span style={{
          background: 'rgba(0,0,0,0.25)',
          borderRadius: 4,
          padding: '2px 7px',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          ADMIN SESSION
        </span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Acting as{' '}
          <strong style={{ fontWeight: 700 }}>{impersonatedUser.displayName}</strong>
          {impersonatedUser.displayName !== impersonatedUser.email && (
            <span style={{ opacity: 0.75 }}> ({impersonatedUser.email})</span>
          )}
          {' '}— {roleLabel}
        </span>
      </div>

      {/* Right: timer + exit */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <span style={{
          fontSize: 12,
          opacity: isExpiringSoon ? 1 : 0.75,
          color: isExpiringSoon ? '#fde68a' : 'inherit',
          whiteSpace: 'nowrap',
        }}>
          {timeLabel}
        </span>
        <button
          onClick={handleExit}
          disabled={exiting}
          data-testid="impersonation-exit-button"
          style={{
            background: '#ffffff',
            color: '#92400e',
            border: 'none',
            borderRadius: 4,
            padding: '5px 12px',
            fontWeight: 700,
            fontSize: 12,
            cursor: exiting ? 'not-allowed' : 'pointer',
            opacity: exiting ? 0.7 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {exiting ? 'Exiting...' : 'Exit Session'}
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
// CB:END impersonation-banner
